import { Avatar, Stack, Typography } from '@mui/material';
import { ITherapySuppliersResult } from 'API/Suppliers/suppliers';
import { SuppliersContext } from 'Context/SuppliersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary } from 'Hooks';
import { Download2 } from 'Images';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import {
  ISupplier,
  getSupplierResource,
  supplierResourceFilterMapper,
} from 'Utils/suppliersResources';
import { useContext } from 'react';

export const Download = () => {
  const { suppliers, resources, expertise } = useContext(SuppliersContext);

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage
    .therapy;

  const findCity = (cityId: number | undefined) => {
    if (!cityId) return '';
    const city = cities?.find((city: any) => city.id === cityId)?.value;
    return city ? ` ${city}` : '';
  };

  const downloadHandler = () => {
    const { filteredResults } = suppliers;

    const csvContent =
      dictionary.csv.join(',').concat('\n') +
      filteredResults
        .map((result: ITherapySuppliersResult) => {
          const {
            name,
            professions,
            specialties,
            firstStreet,
            firstHouseNumber,
            firstCityId,
            secondStreet,
            secondHouseNumber,
            secondCityId,
            cellPhonePrefix,
            cellPhoneNumber,
            homePhonePrefix,
            homePhoneNumber,
            email,
            hasAccessibilityForHandicapped,
            treatmentMethods,
            languages,
            academicDegreeId,
            instituteName,
          } = result;

          const profession = getSupplierResource(
            resources.OCCUP,
            professions[0]
          );

          const supplierSpecialties = supplierResourceFilterMapper(
            expertise as ISupplier,
            specialties
          ).join(' - ');

          const firstAddress = `${firstStreet || ''} ${
            firstHouseNumber || ''
          } ${findCity(firstCityId)}`;
          const secondAddress = `${secondStreet || ''} ${
            secondHouseNumber || ''
          } ${findCity(secondCityId)}`;

          const firstPhoneNumber = cellPhonePrefix
            ? `0${cellPhonePrefix}-${cellPhoneNumber}`
            : '';
          const secondPhoneNumber = homePhonePrefix
            ? `0${homePhonePrefix}-${homePhoneNumber}`
            : '';

          const supplierEmail = (email || '').toLocaleLowerCase();

          const isAccessible = hasAccessibilityForHandicapped
            ? dictionary.accessible
            : '';

          const methods = supplierResourceFilterMapper(
            resources.GISHA,
            treatmentMethods
          ).join(' - ');

          const supplierLng = supplierResourceFilterMapper(
            resources.LNGCD,
            languages
          ).join(' - ');

          const degree = getSupplierResource(resources.TOARA, academicDegreeId);

          const institute = instituteName || '';

          return `${name},${profession},${supplierSpecialties},${firstAddress},${secondAddress},${firstPhoneNumber},${secondPhoneNumber},${supplierEmail},${isAccessible},${methods},${supplierLng},${degree},${institute}`;
        })
        .join('\n');

    const blob = new Blob([`\ufeff${csvContent}`], {
      type: 'text/csv;charset=utf-8',
    });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute(
      'download',
      `${dictionary.mentalSuppliersCsvFileName}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack
      direction='row'
      alignItems='center'
      gap='7px'
      sx={{
        width: 'fit-content',
        ':hover': {
          cursor: 'pointer',
          textDecoration: 'underline',
        },
      }}
      tabIndex={0}
      component='a'
      onClick={downloadHandler}
      onKeyDown={onEnterOrEscapeKeyDown}
    >
      <Typography
        variant='body3'
        sx={{ color: 'primary.main', whiteSpace: 'nowrap' }}
      >
        {Dictionary[DICTIONARY.Suppliers].resultPage.downloadAsFile}
      </Typography>
      <Avatar alt='' src={Download2} sx={{ width: '24px', height: '24px' }} />
    </Stack>
  );
};
